(() => {
  if (window.EDGProductCustomizationLoaded) return;
  window.EDGProductCustomizationLoaded = true;

  const getFormControls = (form) => Array.from(form?.elements || []);

  const getSelectedOption = (form, optionName) => {
    const controls = getFormControls(form);
    const expectedName = `options[${optionName}]`.toLowerCase();
    const select = controls.find((el) => el.tagName === 'SELECT' && String(el.name || '').toLowerCase() === expectedName);
    if (select) return select.value;
    const checked = controls.find((el) => el.matches?.('input[type="radio"][data-option-name]:checked') && String(el.dataset.optionName || '').toLowerCase() === optionName.toLowerCase());
    return checked ? checked.value : '';
  };

  const getFiOptionValue = (form) => getSelectedOption(form, 'Fi Series') || getSelectedOption(form, 'Fi Series - See Images');

  const normalizeWidth = (value) => {
    const text = String(value || '').toLowerCase();
    if (text.includes('3/4')) return '3/4 inch';
    if (text.includes('1 inch') || text.includes('1\"') || text.includes('1”')) return '1 inch';
    return '';
  };

  const getEncodedWidth = (form) => normalizeWidth(getSelectedOption(form, 'Webbing Width')) || normalizeWidth(getFiOptionValue(form)) || normalizeWidth(getSelectedOption(form, 'Size'));
  const getWidth = (root, form) => getEncodedWidth(form) || root.querySelector('[data-edg-manual-width]')?.value || '';
  const selectedValue = (root, selector) => root.querySelector(`${selector}:checked`)?.value || 'No';

  const setFormError = (root, message) => {
    const productForm = root.closest('product-form');
    if (productForm && typeof productForm.handleErrorMessage === 'function') {
      productForm.handleErrorMessage(message || false);
      return;
    }
    const wrapper = productForm?.querySelector('.product-form__error-message-wrapper');
    const field = wrapper?.querySelector('.product-form__error-message');
    if (wrapper && field) {
      wrapper.toggleAttribute('hidden', !message);
      field.textContent = message || '';
    }
  };

  const parseSizeRange = (sizeValue) => {
    const text = String(sizeValue || '');
    if (/custom/i.test(text)) return { custom: true };
    const numbers = text.match(/\d+(?:\.\d+)?/g);
    if (!numbers || numbers.length < 2) return null;
    return { min: Number(numbers[0]), max: Number(numbers[1]), custom: false };
  };

  const isBuckleEligible = (sizeValue) => {
    const text = String(sizeValue || '').trim().toLowerCase();
    if (!text || text.includes('custom')) return false;
    return /^(large\b|x[- ]?large\b|xx[- ]?large\b|xxx[- ]?large\b|xxxx[- ]?large\b|2xl\b|3xl\b|4xl\b)/i.test(text);
  };

  const syncBuckleEligibility = (root, form) => {
    if (root.dataset.isMartingale !== 'true') return;
    const sizeValue = getSelectedOption(form, 'Size');
    const yes = root.querySelector('[data-edg-buckle-yes]');
    const no = root.querySelector('[data-edg-buckle-no]');
    const note = root.querySelector('[data-edg-buckle-note]');
    if (!yes) return;
    const eligible = isBuckleEligible(sizeValue);
    yes.disabled = !eligible;
    if (!eligible && yes.checked && no) no.checked = true;
    if (note) {
      if (!sizeValue) note.textContent = 'Tactical Buckle is available on Large and larger Martingale sizes.';
      else if (eligible) note.textContent = `Tactical Buckle is available for ${sizeValue}.`;
      else if (/custom/i.test(sizeValue)) note.textContent = 'Tactical Buckle requires a confirmed Large-or-larger size; Custom sizing is reviewed before this upgrade is added.';
      else note.textContent = `Tactical Buckle is not available for ${sizeValue}. Choose Large or larger to add it.`;
    }
  };

  const validateMartingaleFit = (root, form, showSuccess = false) => {
    if (root.dataset.isMartingale !== 'true') return { valid: true };
    const neckInput = root.querySelector('[data-edg-neck]');
    const headInput = root.querySelector('[data-edg-head]');
    const status = root.querySelector('[data-edg-fit-status]');
    const buckle = selectedValue(root, '[data-edg-buckle]');
    const sizeValue = getSelectedOption(form, 'Size');
    const neck = Number(neckInput?.value || 0);
    const head = Number(headInput?.value || 0);
    if (buckle === 'Yes' && !isBuckleEligible(sizeValue)) return { valid: false, message: 'Tactical Buckle is available only on Large and larger Martingale sizes.' };
    if (!neck) {
      if (status) { status.dataset.state = 'error'; status.textContent = 'Enter the neck measurement before adding this collar to the cart.'; }
      return { valid: false, message: 'Enter the neck measurement for the Martingale collar.' };
    }
    if (buckle !== 'Yes' && !head) {
      if (status) { status.dataset.state = 'error'; status.textContent = 'Enter the widest head measurement for a slip-over Martingale.'; }
      return { valid: false, message: 'Enter the widest head measurement for the Martingale collar.' };
    }
    const target = buckle === 'Yes' ? neck : Math.max(neck, head);
    const range = parseSizeRange(sizeValue);
    if (range && !range.custom && (target < range.min || target > range.max)) {
      const basis = buckle === 'Yes' ? 'neck measurement' : 'larger neck/head measurement';
      const message = `The selected size (${sizeValue}) does not include the ${basis} of ${target} inches.`;
      if (status) { status.dataset.state = 'error'; status.textContent = message; }
      return { valid: false, message };
    }
    if (status) {
      status.dataset.state = '';
      status.textContent = showSuccess ? (range?.custom ? `Custom size selected. Fit measurement to send with the order: ${target} inches.` : `Fit check: ${target} inches is within the selected size range.`) : '';
    }
    return { valid: true, target };
  };

  const updateUI = (root, form) => {
    syncBuckleEligibility(root, form);
    const buckle = selectedValue(root, '[data-edg-buckle]');
    const head = root.querySelector('[data-edg-head]');
    const fitHelp = root.querySelector('[data-edg-fit-help]');
    if (head) head.required = buckle !== 'Yes';
    if (fitHelp) fitHelp.textContent = buckle === 'Yes' ? 'With the buckle selected, the collar opens. Neck measurement drives the size check; widest-head measurement is optional.' : 'Without a buckle, measure the neck and the widest part of the head directly behind the ears. Select a size that includes the larger measurement.';

    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    const nameplateFields = root.querySelector('[data-edg-nameplate-fields]');
    const engravingInputs = root.querySelectorAll('[data-edg-nameplate-line]');
    const nameplateEnabled = nameplate !== 'No';
    if (nameplateFields) nameplateFields.hidden = !nameplateEnabled;
    engravingInputs.forEach((input) => { input.disabled = !nameplateEnabled; });

    const encodedWidth = getEncodedWidth(form);
    const widthFallback = root.querySelector('[data-edg-width-fallback]');
    const manualWidth = root.querySelector('[data-edg-manual-width]');
    if (widthFallback && manualWidth) {
      const needsManualWidth = root.dataset.isFi === 'true' && root.dataset.hasWebbing === 'true' && !encodedWidth;
      widthFallback.hidden = !needsManualWidth;
      manualWidth.disabled = !needsManualWidth;
      manualWidth.required = needsManualWidth;
      if (!needsManualWidth) manualWidth.value = '';
    }
    validateMartingaleFit(root, form, Boolean(root.querySelector('[data-edg-neck]')?.value));
  };

  const extractMainProperties = (formData) => {
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && String(value).trim() !== '') properties[match[1]] = String(value).trim();
    }
    return properties;
  };

  const nameplateProperties = (root, width) => {
    const properties = { 'For Collar': root.dataset.productTitle || 'Collar' };
    if (width) properties['Collar Webbing Width'] = width;
    root.querySelectorAll('[data-edg-nameplate-line]').forEach((input) => {
      const value = input.value.trim();
      if (!value) return;
      const propertyKey = input.dataset.edgNameplateProperty || `Engraving Line ${input.dataset.edgNameplateLine}`;
      properties[propertyKey] = value;
    });
    return properties;
  };

  const commonAddonProperties = (root, width) => {
    const properties = { 'For Collar': root.dataset.productTitle || 'Collar' };
    if (width) properties['Collar Webbing Width'] = width;
    return properties;
  };

  const buildAddonItems = (root, quantity, width) => {
    const items = [];
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    const idAnchor = selectedValue(root, '[data-edg-id-anchor]');
    const nightguard = selectedValue(root, '[data-edg-nightguard]');
    const buckle = selectedValue(root, '[data-edg-buckle]');
    if (nameplate === 'Stainless Steel') items.push({ id: Number(root.dataset.nameplateStainlessVariant), quantity, properties: nameplateProperties(root, width) });
    else if (nameplate === 'Brass') items.push({ id: Number(root.dataset.nameplateBrassVariant), quantity, properties: nameplateProperties(root, width) });
    if (idAnchor === 'Yes') items.push({ id: Number(root.dataset.idAnchorVariant), quantity, properties: commonAddonProperties(root, width) });
    if (nightguard === 'Yes') items.push({ id: Number(root.dataset.nightguardVariant), quantity, properties: commonAddonProperties(root, width) });
    if (buckle === 'Yes') items.push({ id: Number(root.dataset.tacticalBuckleVariant), quantity, properties: commonAddonProperties(root, width) });
    return items.filter((item) => Number.isFinite(item.id) && item.id > 0);
  };

  const validateCustomizer = (root, form) => {
    const width = getWidth(root, form);
    const manualWidth = root.querySelector('[data-edg-manual-width]');
    if (manualWidth && !manualWidth.disabled && !width) return { valid: false, message: 'Select the webbing width for this collar.' };
    const fit = validateMartingaleFit(root, form, true);
    if (!fit.valid) return fit;
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    if (nameplate !== 'No') {
      const hasEngraving = Array.from(root.querySelectorAll('[data-edg-nameplate-line]')).some((input) => input.value.trim() !== '');
      if (!hasEngraving) return { valid: false, message: 'Enter at least one engraving field for the nameplate.' };
      if (!width) return { valid: false, message: 'Select or confirm the webbing width before adding a nameplate.' };
    }
    return { valid: true, width };
  };

  const addConfiguredItems = async (root, form, evt) => {
    const validation = validateCustomizer(root, form);
    if (!validation.valid) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      setFormError(root, validation.message);
      return;
    }
    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));
    const quantity = Math.max(1, parseInt(formData.get('quantity'), 10) || 1);
    if (!variantId) return;
    const addonItems = buildAddonItems(root, quantity, validation.width);
    if (!addonItems.length) { setFormError(root, false); return; }

    evt.preventDefault();
    evt.stopImmediatePropagation();
    setFormError(root, false);
    const productForm = root.closest('product-form');
    const submitButton = productForm?.querySelector('[type="submit"]');
    const spinner = productForm?.querySelector('.loading__spinner');
    submitButton?.setAttribute('aria-disabled', 'true');
    submitButton?.classList.add('loading');
    spinner?.classList.remove('hidden');

    const mainProperties = extractMainProperties(formData);
    const nameplate = selectedValue(root, '[data-edg-nameplate]');
    const idAnchor = selectedValue(root, '[data-edg-id-anchor]');
    const nightguard = selectedValue(root, '[data-edg-nightguard]');
    const buckle = selectedValue(root, '[data-edg-buckle]');
    if (validation.width) mainProperties['Webbing Width Confirmation'] = validation.width;
    if (root.dataset.allowsNameplate === 'true') mainProperties['Nameplate'] = nameplate;
    if (root.dataset.hasWebbing === 'true') {
      mainProperties['ID Anchor'] = idAnchor;
      mainProperties['NightGuard Reflective Edge'] = nightguard;
    }
    if (root.dataset.isMartingale === 'true') mainProperties['Tactical Buckle'] = buckle;

    const payload = { items: [{ id: variantId, quantity, properties: mainProperties }, ...addonItems] };
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (cart?.getSectionsToRender) {
      payload.sections = cart.getSectionsToRender().map((section) => section.id);
      payload.sections_url = window.location.pathname;
      cart.setActiveElement?.(document.activeElement);
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Unable to add this configuration to the cart.');
      if (cart?.renderContents && data.sections) {
        if (cart.classList?.contains('is-empty')) cart.classList.remove('is-empty');
        cart.renderContents(data);
      } else window.location = window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;
    } catch (error) {
      setFormError(root, error.message || 'Unable to add this configuration to the cart.');
    } finally {
      submitButton?.classList.remove('loading');
      submitButton?.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  };

  const isProductOptionControl = (el) => {
    if (el.tagName === 'SELECT' && String(el.name || '').startsWith('options[')) return true;
    return Boolean(el.matches?.('input[type="radio"][data-option-name]'));
  };

  const initRoot = (root) => {
    if (root.dataset.edgInitialized === 'true') return;
    const productForm = root.closest('product-form');
    const form = productForm?.querySelector('form');
    if (!form) return;
    root.dataset.edgInitialized = 'true';
    root.addEventListener('change', () => updateUI(root, form));
    root.addEventListener('input', (event) => {
      if (event.target.matches('[data-edg-neck], [data-edg-head]')) validateMartingaleFit(root, form, true);
    });
    getFormControls(form).filter(isProductOptionControl).forEach((control) => control.addEventListener('change', () => window.setTimeout(() => updateUI(root, form), 0)));
    form.addEventListener('submit', (evt) => addConfiguredItems(root, form, evt), true);
    updateUI(root, form);
  };

  const initAll = () => document.querySelectorAll('[data-edg-collar-customizer]').forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, { once: true });
  else initAll();
})();
